import { defineComponent as _defineComponent } from 'vue'
import { nextTick, ref, watch } from 'vue'
import { debounce } from '~/utils/debounce'


export type ChipOption = {
  label: string
  value: Record<string, any>
}

export default /*#__PURE__*/_defineComponent({
  __name: 'UIChipInput',
  props: {
    value: null,
    searchResults: null,
    placeholder: null,
    autoRefocus: { type: Boolean }
  },
  emits: ["input", "search", "searchOpenStateChanged"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'input', value: ChipOption[]): void
  (e: 'search', value: string): void
  (e: 'searchOpenStateChanged', value: boolean): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: ChipOption[]
  searchResults?: ChipOption[]
  placeholder: string
  autoRefocus?: boolean
};





const searchInputFieldRef = ref<HTMLInputElement | null>(null)
const addMoreBtnRef = ref<HTMLSpanElement | null>(null)
const searchBoxRef = ref<HTMLDivElement | null>(null)
const resultsListRef = ref<HTMLUListElement | null>(null)

const inputFieldValue = ref<string>('')
const activeIndex = ref<number>(0)
const isSearchBoxOpen = ref(false)

// Handle search box visibility
function handleShowSearchBox() {
  isSearchBoxOpen.value = true
  nextTick(() => {
    searchInputFieldRef.value?.focus()
    if (resultsListRef.value) {
      resultsListRef.value.scrollTop = 0
    }
  })
}

function handleCloseSearchBox(event: MouseEvent) {
  const target = event.target as Node
  const triggerElement = addMoreBtnRef.value
  const searchBoxElement = searchBoxRef.value
  const resultsList = resultsListRef.value

  // Check if click is inside results list
  const clickedInsideResults = resultsList ? resultsList.contains(target) : false

  // Don't close if clicked inside results list
  if (clickedInsideResults) {
    return
  }

  const clickedOutsideTrigger = triggerElement ? !triggerElement.contains(target) : true
  const clickedOutsideSearchBox = searchBoxElement ? !searchBoxElement.contains(target) : true

  if (clickedOutsideTrigger && clickedOutsideSearchBox) {
    if (isSearchBoxOpen.value) {
      isSearchBoxOpen.value = false
      inputFieldValue.value = ''
      activeIndex.value = 0
    }
  }
}

// Keyboard navigation
function selectPreviousChip() {
  if (activeIndex.value > 0) {
    activeIndex.value--
    nextTick(scrollToActiveItem)
  }
}

function selectNextChip() {
  if (!props.searchResults) return
  if (activeIndex.value < props.searchResults?.length - 1) {
    activeIndex.value++
    nextTick(scrollToActiveItem)
  }
}

function scrollToActiveItem() {
  if (!resultsListRef.value) return
  const activeItem = resultsListRef.value.children[activeIndex.value] as HTMLElement
  if (activeItem) {
    activeItem.scrollIntoView({ block: 'nearest' })
  }
}

function handleEnterKey() {
  handleSubmitChip()
}

// Handle chip updates with parent component
function handleSubmitChip(index?: number) {
  if (!props.searchResults) return

  const selectedIndex = index !== undefined ? index : activeIndex.value
  if (selectedIndex < 0 || selectedIndex >= props.searchResults.length) return

  const result = props.searchResults[selectedIndex]
  inputFieldValue.value = ''

  // Check if option is already selected by comparing values
  const alreadySelected = props.value.some((item) => item.label === result.label)
  if (!alreadySelected) {
    emits('input', [...props.value, result])
  }
  activeIndex.value = 0

  if (props.autoRefocus) {
    // Re-focus input field immediately and after nextTick to ensure it gets focus
    if (searchInputFieldRef.value) {
      searchInputFieldRef.value.focus()
      nextTick(() => {
        searchInputFieldRef.value?.focus()
      })
    }
  } else {
    handleCloseSearchBox(new MouseEvent('click'))
  }
}

function handleDeleteChip(chip: ChipOption) {
  emits(
    'input',
    props.value.filter((c) => c.value !== chip.value)
  )
}

// Handle search input
function emitSearch(value: string) {
  emits('search', value)
}

const debouncedEmitSearch = debounce(emitSearch, 500)

watch(inputFieldValue, (newValue) => {
  debouncedEmitSearch(newValue)
})

watch(
  () => isSearchBoxOpen.value,
  (newValue) => {
    emits('searchOpenStateChanged', newValue)
  },
  {
    immediate: true
  }
)

watch(
  () => props.searchResults,
  (newValue) => {
    if (newValue && newValue.length > 0) {
      activeIndex.value = 0
      scrollToActiveItem()
    }
  }
)

return { __sfc: true,props, emits, searchInputFieldRef, addMoreBtnRef, searchBoxRef, resultsListRef, inputFieldValue, activeIndex, isSearchBoxOpen, handleShowSearchBox, handleCloseSearchBox, selectPreviousChip, selectNextChip, scrollToActiveItem, handleEnterKey, handleSubmitChip, handleDeleteChip, emitSearch, debouncedEmitSearch }
}

})